import Vue from 'vue'
import Router from 'vue-router'
import store from '@/store/store'
import { getAssignedRoles, resolveLandingPathForWorkspace, setActiveWorkspaceRole } from '@/utils/role-dashboard'
import { inferRolesFromPath } from '@/config/workspace'
import { getPreferredLandingPage, rememberVisitedPage } from '@/utils/workspace-preferences'

// Containers
const TheContainer = () => import('@/containers/TheContainer')
const TheContainer_Landing = () => import('@/containers/TheContainer_Landing.vue')
const TheContainer_Project = () => import('@/containers/TheContainer_Project')


// Views
const Dashboard = () => import('@/views/Dashboard')

// Views GoogleMaps
const GoogleMaps = () => import('@/views/GoogleMaps')

// Views - Pages
const Page404 = () => import('@/views/pages/Page404')
const Page500 = () => import('@/views/pages/Page500')
const Login = () => import('@/views/pages/Login')
const Register = () => import('@/views/pages/Register')

const CreateGroup = () => import('@/projects/views/security/CreateGroup')
const CreateMenu = () => import('@/projects/views/security/CreateMenu')
const PermissionMatrix = () => import('@/projects/views/security/PermissionMatrix')
const AuditExplorer = () => import('@/projects/views/security/AuditExplorer')
const MCARegistry = () => import('@/projects/views/mca/MCARegistry')
const BusinessOperations = () => import('@/projects/views/operations/BusinessOperations')
const ReceiveForm = () => import('@/projects/views/ocr/ReceiveForm')
const SettingMessage = () => import('@/projects/views/setting/Message')
const SettingVerification = () => import('@/projects/views/setting/Verification')
const SettingGroup = () => import('@/projects/views/setting/Group')
const SettingMessageAuthen = () => import('@/projects/views/setting/MessageAuthen')
const SettingMessageStatus = () => import('@/projects/views/setting/Status')
const EmailNotifications = () => import('@/projects/views/setting/EmailNotifications')
const WorkflowActions = () => import('@/projects/views/setting/WorkflowActions')
const RuntimeAccess = () => import('@/projects/views/setting/RuntimeAccess')
const DatabaseBackup = () => import('@/projects/views/setting/DatabaseBackup')
const AccountManagement = () => import('@/projects/views/accounts/Management')
const TrainingRequests = () => import('@/projects/views/training/Requests')
const TrainingRecords = () => import('@/projects/views/training/Records')
const AgreementsPage = () => import('@/views/admin/Agreements')
const AdminAgreementDetailPage = () => import('@/views/admin/AdminAgreementDetail')
const AuditLogPage = () => import('@/views/admin/AuditLog')
const ClauseLibraryPage = () => import('@/views/admin/ClauseLibrary')
const ReviewQueuePage = () => import('@/views/admin/ReviewQueue')
const AdminAssignCommitteePage = () => import('@/views/admin/AdminAssignCommittee')
const AdminFinalApprovalPage = () => import('@/views/admin/AdminFinalApproval')
const TranslationRequestsPage = () => import('@/views/admin/TranslationRequests')
const ReportsPage = () => import('@/views/admin/Reports')
const NotificationsPage = () => import('@/views/admin/Notifications')
const ReviewPage = () => import('@/views/admin/Review')
const ReviewHistoryPage = () => import('@/views/admin/ReviewHistory')
const GuidelinePage = () => import('@/views/admin/Guideline')
const HelpPage = () => import('@/views/admin/Help')
const ProfilePage = () => import('@/views/admin/Profile')
const PreferencesPage = () => import('@/views/admin/Preferences')
const UserAgreementPage = () => import('@/views/user/UserAgreement')
const UserClauseLibraryPage = () => import('@/views/user/UserClauseLibrary')
const UserNewAgreementPage = () => import('@/views/user/UserNewAgreement')
const UserExchangeAgreementPage = () => import('@/views/user/UserExchangeAgreement')
const UserDoubleDegreeAgreementPage = () => import('@/views/user/UserDoubleDegreeAgreement')
const UserExtensionAgreementPage = () => import('@/views/user/UserExtensionAgreement')
const UserMOUAgreementPage = () => import('@/views/user/UserMOUAgreement')
const UserSelectClausesPage = () => import('@/views/user/UserSelectClauses')
const UserReviewSummaryPage = () => import('@/views/user/UserReviewSummary')
const UserReviewQueuePage = () => import('@/views/user/UserReviewQueue')
const UserStartReviewPage = () => import('@/views/user/UserStartReview')
const UserTranslationPage = () => import('@/views/user/UserTranslation')
const UserReportsPage = () => import('@/views/user/UserReports')
const UserGuidelinePage = () => import('@/views/user/UserGuideline')
const ProjectSettingsGeneralPage = () => import('@/views/project/ProjectSettingsGeneral')
const ProjectSettingsPermissionPage = () => import('@/views/project/ProjectSettingsPermission')


//
const DashboardUser = () => import('@/views/dashboard/User')
const DashboardAdmin = () => import('@/views/dashboard/Admin')
const DashboardCommittee = () => import('@/views/dashboard/Committee')


Vue.use(Router)

const router = new Router({
  hash: false,
  mode: "history", // https://router.vuejs.org/api/#mode
  linkActiveClass: 'open active',
  scrollBehavior: () => ({ y: 0 }),
  routes: [




    {
      path: '/',
      redirect: '/dashboard',
      name: 'Home',
      component: TheContainer,
      children: [
        {
          path: 'dashboard',
          name: 'Dashboard',
          meta: {
            pageTitleKey: 'routes.dashboard.title',
            pageTitle: 'Dashboard',
            pageDescription: 'Overview of the active workspace and available role dashboards.'
          },
          component: Dashboard
        },
        {
          path: 'dashboard/admin',
          name: 'Admin Dashboard',
          meta: {
            pageTitleKey: 'routes.adminDashboard.title',
            pageTitle: 'Admin Dashboard',
            pageDescription: 'System-wide visibility for agreements, permissions, analytics, and audit activity.'
          },
          component: DashboardAdmin
        },
        {
          path: 'dashboard/committee',
          name: 'Committee Dashboard',
          meta: {
            pageTitleKey: 'routes.committeeDashboard.title',
            pageTitle: 'Committee Dashboard',
            pageDescription: 'Review assignments, decisions, history, and guideline updates for committee members.'
          },
          component: DashboardCommittee
        },
        {
          path: 'mca/registry',
          name: 'MCA Registry',
          meta: {
            pageTitle: 'MCA Registry',
            pageDescription: 'Track MCA agreements, ownership, review state, and renewal timing in one IAM-protected workspace.',
            permission: { path: '/mca/registry', action: 'view' }
          },
          component: MCARegistry
        },
        {
          path: 'operations/business',
          name: 'Business Operations',
          meta: {
            pageTitle: 'Business Operations',
            pageDescription: 'Monitor operating workstreams, decisions, timelines, and access flow.',
            permission: { path: '/operations/business', action: 'view' }
          },
          component: BusinessOperations
        },
        {
          path: 'ocr/receive-form',
          name: 'OCR Receive Form',
          meta: {
            pageTitle: 'OCR Receive Form',
            pageDescription: 'Record document intake, upload files, and review OCR mock results.',
            permission: { path: '/ocr/receive-form', action: 'view' }
          },
          component: ReceiveForm
        },
        {
          path: 'agreements',
          name: 'Agreements',
          meta: {
            pageTitle: 'Agreements',
            pageDescription: 'Browse, monitor, and organize agreement records across the workflow.',
            nextStepTitle: 'Add a searchable data table',
            nextStepCopy: 'This module is ready for list, filter, detail drawer, and status controls.'
          },
          component: AgreementsPage
        },
        {
          path: 'agreements/:id',
          name: 'Agreement Details',
          meta: {
            pageTitle: 'Agreement Details',
            pageDescription: 'Read the full agreement record and document content.'
          },
          component: AdminAgreementDetailPage
        },
        {
          path: 'audit-log',
          name: 'Audit Log',
          meta: {
            pageTitle: 'Audit Log',
            pageDescription: 'Review system events, changes, and traceable access history.',
            nextStepTitle: 'Connect audit events',
            nextStepCopy: 'A timeline or table view fits well here once the backend feed is connected.'
          },
          component: AuditLogPage
        },
        {
          path: 'clause-library',
          name: 'Clause Library',
          meta: {
            pageTitle: 'Clause Library',
            pageDescription: 'Store approved clauses and reusable language for faster drafting and review.'
          },
          component: ClauseLibraryPage
        },
        {
          path: 'review-queue',
          name: 'Review Queue',
          meta: {
            pageTitle: 'Review Queue',
            pageDescription: 'Track pending items, priorities, and review ownership in one queue.'
          },
          component: ReviewQueuePage
        },
        {
          path: 'review-queue/:id/assign-committee',
          name: 'Assign Committee',
          meta: {
            pageTitle: 'Assign Committee',
            pageDescription: 'Assign committee members and send the agreement into committee review.'
          },
          component: AdminAssignCommitteePage
        },
        {
          path: 'review-queue/:id/review',
          name: 'Admin Review',
          meta: {
            pageTitle: 'Review',
            pageDescription: 'Monitor committee participation, comments, and document status from one shared admin review workspace.'
          },
          component: AdminFinalApprovalPage
        },
        {
          path: 'review-queue/:id/final-approval',
          name: 'Final Approval',
          meta: {
            pageTitle: 'Final Approval',
            pageDescription: 'Review committee feedback and complete the final admin approval decision.'
          },
          component: AdminFinalApprovalPage
        },
        {
          path: 'translation-requests',
          name: 'Translation Requests',
          meta: {
            pageTitle: 'Translation Requests',
            pageDescription: 'Manage requests, statuses, and turnaround for language support.'
          },
          component: TranslationRequestsPage
        },
        {
          path: 'reports',
          name: 'Reports',
          meta: {
            pageTitle: 'Reports & Analytics',
            pageDescription: 'Review KPIs, trend summaries, and exportable reporting outputs.'
          },
          component: ReportsPage
        },
        {
          path: 'review',
          name: 'Review',
          meta: {
            pageTitleKey: 'routes.review.title',
            pageTitle: 'Review',
            pageDescription: 'Open assigned cases and complete committee review actions.'
          },
          component: ReviewPage
        },
        {
          path: 'review-history',
          name: 'Review History',
          meta: {
            pageTitleKey: 'routes.reviewHistory.title',
            pageTitle: 'Review History',
            pageDescription: 'Look back through completed reviews and prior committee decisions.'
          },
          component: ReviewHistoryPage
        },
        {
          path: 'guideline',
          name: 'Guideline',
          meta: {
            pageTitleKey: 'routes.guideline.title',
            pageTitle: 'Guideline',
            pageDescription: 'Publish and review operational guidance, rules, and reference material.'
          },
          component: GuidelinePage
        },
        {
          path: 'help',
          name: 'Help & Support',
          meta: {
            pageTitleKey: 'routes.help.title',
            pageTitle: 'Help & Support',
            pageDescription: 'Provide FAQs, contact channels, and support resources for users.'
          },
          component: HelpPage
        },
        {
          path: 'dashboard/user-agreements',
          name: 'User Agreements',
          meta: {
            pageTitleKey: 'routes.userAgreements.title',
            pageTitle: 'My Agreements',
            pageDescription: 'Create, submit, and track your agreement workflow.'
          },
          component: UserAgreementPage
        },
        {
          path: 'dashboard/user-clause-library',
          name: 'User Clause Library',
          meta: {
            pageTitleKey: 'routes.userClauseLibrary.title',
            pageTitle: 'My Clause Library',
            pageDescription: 'Reuse starter clauses and manage your own clause library.'
          },
          component: UserClauseLibraryPage
        },
        {
          path: 'dashboard/user-review-queue',
          name: 'User Review Queue',
          meta: {
            pageTitleKey: 'routes.userReviewQueue.title',
            pageTitle: 'My Review Queue',
            pageDescription: 'Follow agreement progress through admin and committee review.'
          },
          component: UserReviewQueuePage
        },
        {
          path: 'dashboard/user-translation',
          name: 'User Translation',
          meta: {
            pageTitleKey: 'routes.userTranslation.title',
            pageTitle: 'My Translation Requests',
            pageDescription: 'Request translation support for agreement drafts and track progress.'
          },
          component: UserTranslationPage
        },
        {
          path: 'dashboard/user-reports',
          name: 'User Reports',
          meta: {
            pageTitleKey: 'routes.userReports.title',
            pageTitle: 'My Reports',
            pageDescription: 'Review your agreement and translation activity in one place.'
          },
          component: UserReportsPage
        },
        {
          path: 'dashboard/user-guideline',
          name: 'User Guideline',
          meta: {
            pageTitleKey: 'routes.userGuideline.title',
            pageTitle: 'User Guideline',
            pageDescription: 'Read the current user workflow and agreement guidance.'
          },
          component: UserGuidelinePage
        },
        {
          path: 'dashboard/user-agreements/new',
          name: 'User New Agreement',
          meta: {
            pageTitleKey: 'routes.userNewAgreement.title',
            pageTitle: 'Create Agreement',
            pageDescription: 'Start a new agreement draft.'
          },
          component: UserNewAgreementPage
        },
        {
          path: 'dashboard/user-agreements/new/exchange',
          name: 'User New Exchange Agreement',
          component: UserExchangeAgreementPage
        },
        {
          path: 'dashboard/user-agreements/new/double-degree',
          name: 'User New Double Degree Agreement',
          component: UserDoubleDegreeAgreementPage
        },
        {
          path: 'dashboard/user-agreements/new/extension',
          name: 'User New Extension Agreement',
          component: UserExtensionAgreementPage
        },
        {
          path: 'dashboard/user-agreements/new/mou',
          name: 'User New MOU Agreement',
          component: UserMOUAgreementPage
        },
        {
          path: 'dashboard/user-agreements/:id/exchange',
          name: 'User Exchange Agreement',
          component: UserExchangeAgreementPage
        },
        {
          path: 'dashboard/user-agreements/:id/double-degree',
          name: 'User Double Degree Agreement',
          component: UserDoubleDegreeAgreementPage
        },
        {
          path: 'dashboard/user-agreements/:id/extension',
          name: 'User Extension Agreement',
          component: UserExtensionAgreementPage
        },
        {
          path: 'dashboard/user-agreements/:id/mou',
          name: 'User MOU Agreement',
          component: UserMOUAgreementPage
        },
        {
          path: 'dashboard/user-agreements/:id/clauses',
          name: 'User Select Clauses',
          component: UserSelectClausesPage
        },
        {
          path: 'dashboard/user-agreements/:id/review-summary',
          name: 'User Review Summary',
          component: UserReviewSummaryPage
        },
        {
          path: 'dashboard/user-agreements/:id/start-review',
          name: 'User Start Review',
          component: UserStartReviewPage
        },
        {
          path: 'permission',
          redirect: '/security/permissions/menu',
          name: 'Permission Home'
        },
        {
          path: 'permission/users',
          redirect: '/accounts/directory'
        },
        {
          path: 'permission/roles',
          redirect: '/security/permissions/matrix'
        },
        {
          path: 'permission/assignments',
          redirect: '/security/permissions/matrix'
        },
        {
          path: 'general',
          redirect: '/general/profile',
          name: 'General'
        },
        {
          path: 'general/profile',
          name: 'Profile',
          meta: {
            pageTitleKey: 'routes.profile.title',
            pageTitle: 'Profile',
            pageDescription: 'Update personal details and review your workspace profile settings.'
          },
          component: ProfilePage
        },
        {
          path: 'general/preferences',
          name: 'Preferences',
          meta: {
            pageTitleKey: 'preferences.title',
            pageTitle: 'Preferences',
            pageDescription: 'Manage notifications, language, and default workspace behavior.'
          },
          component: PreferencesPage
        },
        {
          path: 'logout',
          name: 'Logout',
          beforeEnter(to, from, next) {
            store.dispatch('auth/signOut')
            next(false)
          }
        },
        {
          path: 'security/permissions',
          redirect: '/security/permissions/menu',
          name: 'Permission'
        },
        {
          path: 'security/permissions/group',
          name: '2. Create Group',
          meta: { permission: { path: '/security/permissions/group', action: 'view' } },
          component: CreateGroup
        },
        {
          path: 'security/permissions/menu',
          name: '1. Create Menu',
          meta: { permission: { path: '/security/permissions/menu', action: 'view' } },
          component: CreateMenu
        },
        {
          path: 'security/permissions/matrix',
          name: 'MCA Permission Matrix',
          meta: { permission: { path: '/security/permissions/matrix', action: 'view' } },
          component: PermissionMatrix
        },
        {
          path: 'security/assignments',
          redirect: '/security/permissions/matrix'
        },
        {
          path: 'security/audit',
          name: 'Audit Explorer',
          meta: {
            pageTitle: 'Audit Explorer',
            pageDescription: 'Review authentication, account, and lifecycle events recorded by IAM.',
            permission: { path: '/security/audit', action: 'view' }
          },
          component: AuditExplorer
        },
        {
          path: 'config/message-authen',
          name: 'Message Authen',
          meta: { permission: { path: '/config/message-authen', action: 'view' } },
          component: SettingMessageAuthen
        },
        {
          path: 'config/email-notifications',
          name: 'Email Notifications',
          meta: { permission: { path: '/config/email-notifications', action: 'view' } },
          component: EmailNotifications
        },
        {
          path: 'config/workflow-actions',
          name: 'Workflow Actions',
          meta: { permission: { path: '/config/workflow-actions', action: 'view' } },
          component: WorkflowActions
        },
        {
          path: 'config/runtime-access',
          name: 'Runtime Access',
          meta: { permission: { path: '/config/runtime-access', action: 'view' } },
          component: RuntimeAccess
        },
        {
          path: 'config/database-backup',
          name: 'Database Backup',
          meta: { permission: { path: '/config/database-backup', action: 'view' } },
          component: DatabaseBackup
        },
        {
          path: 'config/setting-message',
          name: 'Setting Message',
          meta: { permission: { path: '/config/setting-message', action: 'view' } },
          component: SettingMessage
        },
        {
          path: 'config/verification',
          name: 'Setting Verification',
          meta: { permission: { path: '/config/verification', action: 'view' } },
          component: SettingVerification
        },
        {
          path: 'setting/message-status',
          name: 'Message Status',
          meta: { permission: { path: '/setting/message-status', action: 'view' } },
          component: SettingMessageStatus
        },
        {
          path: 'setting/group',
          name: 'Setting Group',
          meta: { permission: { path: '/setting/group', action: 'view' } },
          component: SettingGroup
        },
        {
          path: 'accounts/directory',
          name: 'Delegated Account Directory',
          meta: {
            pageTitle: 'Account Directory',
            pageDescription: 'Review account records and effective delegated access from IAM.',
            permission: { path: '/accounts/directory', action: 'view' }
          },
          component: AccountManagement
        },
        {
          path: 'accounts/management',
          name: 'Account Management',
          component: AccountManagement
        },
        {
          path: 'training/requests',
          name: 'Training Requests',
          meta: { permission: { path: '/training/requests', action: 'view' } },
          component: TrainingRequests
        },
        {
          path: 'training/records',
          name: 'Training Records',
          meta: { permission: { path: '/training/requests', action: 'view' } },
          component: TrainingRecords
        },
        {
          path: 'google-maps',
          name: 'Google Maps',
          component: GoogleMaps
        },
        {
          path: 'notifications',
          name: 'Notifications',
          meta: {
            pageTitleKey: 'routes.notifications.title',
            pageTitle: 'Notifications',
            pageDescription: 'Review alerts, reminders, and unread updates in one place.'
          },
          component: NotificationsPage
        }
      ]
    },

    {
      path: '/project/:application',
      redirect: to => `/project/${to.params.application}/settings/general`,
      name: 'Project Workspace',
      component: TheContainer_Project,
      children: [
        {
          path: 'settings/general',
          name: 'Project Settings General',
          meta: {
            pageTitle: 'Project General',
            pageDescription: 'Working general-setting templates for the selected project.'
          },
          component: ProjectSettingsGeneralPage
        },
        {
          path: 'settings/permission',
          name: 'Project Settings Permission',
          meta: {
            pageTitle: 'Project Permission',
            pageDescription: 'Working permission-setting templates for the selected project.'
          },
          component: ProjectSettingsPermissionPage
        }
      ]
    },


    {
      path: '/pages',
      redirect: '/pages/404',
      name: 'Pages',
      component: {
        render (c) { return c('router-view') }
      },
      children: [
        {
          path: '404',
          name: 'Page404',
          component: Page404
        },
        {
          path: '500',
          name: 'Page500',
          component: Page500
        },
        {
          path: 'login',
          name: 'Login',
          component: Login
        },
        {
          path: 'register',
          name: 'Register',
          component: Register
        }
      ]
    }
  ]
})

function normalizePermissionPath(path) {
  if (!path) return ''
  let normalized = String(path).trim()
  const queryIndex = normalized.indexOf('?')
  if (queryIndex !== -1) normalized = normalized.slice(0, queryIndex)
  const hashIndex = normalized.indexOf('#')
  if (hashIndex !== -1) normalized = normalized.slice(0, hashIndex)
  normalized = normalized.replace(/\/{2,}/g, '/')
  if (!normalized.startsWith('/')) normalized = `/${normalized}`
  if (normalized.length > 1 && normalized.endsWith('/')) {
    normalized = normalized.slice(0, -1)
  }
  return normalized
}

function swapPermissionPlurality(path) {
  const normalized = normalizePermissionPath(path)
  if (!normalized) return ''
  if (normalized.includes('/permissions')) {
    return normalized.replace('/permissions', '/permission')
  }
  if (normalized.includes('/permission')) {
    return normalized.replace('/permission', '/permissions')
  }
  return ''
}

function buildPermissionCandidates(to, permissionMeta) {
  const candidates = new Set()
  const rawMetaPath = permissionMeta && permissionMeta.path ? permissionMeta.path : ''
  const rawRoutePath = to && to.path ? to.path : ''

  const normalizedMetaPath = normalizePermissionPath(rawMetaPath)
  const normalizedRoutePath = normalizePermissionPath(rawRoutePath)

  ;[
    normalizedMetaPath,
    normalizedRoutePath,
    swapPermissionPlurality(normalizedMetaPath),
    swapPermissionPlurality(normalizedRoutePath)
  ].filter(Boolean).forEach(item => candidates.add(item))

  return Array.from(candidates)
}

function normalizeRoleToken(value) {
  return String(value || '').trim().toLowerCase()
}

function isOwnerProfile(profile) {
  if (!profile || typeof profile !== 'object') return false

  const tokens = new Set()
  const pushRole = (role) => {
    if (!role) return
    if (typeof role === 'string') {
      tokens.add(normalizeRoleToken(role))
      return
    }
    if (Array.isArray(role)) {
      role.forEach(pushRole)
      return
    }
    if (typeof role === 'object') {
      ;[
        role.name,
        role.title,
        role.value,
        role.code,
        role.key,
        role.slug,
        role.role,
        role.roleName,
        role.label,
        role.groupLabel
      ].forEach(item => {
        if (item) pushRole(item)
      })
    }
  }

  pushRole(profile.role)
  pushRole(profile.roleName)
  pushRole(profile.type)
  if (Array.isArray(profile.roles)) profile.roles.forEach(pushRole)
  if (Array.isArray(profile.securityGroups)) profile.securityGroups.forEach(pushRole)

  return tokens.has('owner')
}

function isAdminProfile(profile) {
  return getAssignedRoles(store).includes('admin') || getAssignedRoles(profile).includes('admin')
}

router.beforeEach(async (to, from, next) => {
  try {
    await store.dispatch('auth/bootstrapSession')
  } catch (err) {
    // ignore bootstrap error
  }

  const isPublicPage = to.path.startsWith('/pages')
  const hasToken = !!store.state.XAccessToken
  const authState = store.getters['auth/authenticated'] || {}
  const isAuthenticated = !!authState.isAuthen

  if (!isPublicPage && (!hasToken || !isAuthenticated)) {
    return next({ path: '/pages/login' })
  }

  const profile = store.getters['auth/profile'] || null
  const preferredLandingPage = resolveLandingPathForWorkspace(store, getPreferredLandingPage(''))

  if (to.path === '/pages/login' && hasToken && isAuthenticated) {
    return next(preferredLandingPage)
  }

  if (hasToken && isAuthenticated && to.path === '/dashboard') {
    if (preferredLandingPage !== '/dashboard') {
      return next(preferredLandingPage)
    }
  }

  if ((to.path.startsWith('/permission/roles') || to.path.startsWith('/security/permissions/matrix')) && !isAdminProfile(profile)) {
    return next({ path: '/pages/404' })
  }

  if (to.query && to.query.workspace) {
    setActiveWorkspaceRole(to.query.workspace, store)
  } else if (to.path === '/dashboard') {
    setActiveWorkspaceRole('all', store)
  } else if (hasToken && isAuthenticated) {
    const routeRoles = inferRolesFromPath(to.path)
    if (routeRoles.length === 1) {
      setActiveWorkspaceRole(routeRoles[0], store)
    }
  }

  const permissionMeta = to.meta && to.meta.permission
  const isOwner = isOwnerProfile(profile)
  const isAdmin = isAdminProfile(profile)
  if (permissionMeta && hasToken && isAuthenticated && !isOwner && !isAdmin) {
    if (!store.getters['security/loaded']) {
      try {
        await store.dispatch('security/fetchMyPermissions')
      } catch (err) {
        // permission fetch failed, fallback to denied
      }
    }
    const action = permissionMeta.action || 'view'
    const pathCandidates = buildPermissionCandidates(to, permissionMeta)
    const canAccess = pathCandidates.some(path => (
      store.getters['security/canAccess'](path, action)
    ))
    if (!canAccess) {
      return next({ path: '/pages/404' })
    }
  }

  return next()
})

router.afterEach((to) => {
  rememberVisitedPage(to)
})

export default router
